
import React, { useEffect, useState, useRef } from "react";
import { CiBellOn, CiSearch } from "react-icons/ci";

// Helper: fallback logo
const DEFAULT_LOGO =
  "https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg";

// Dummy user initial data (local simulation, could persist to localStorage)
const DUMMY_USER: User = {
  id: 1,
  name: "Jane Doe",
  email: "janedoe@example.com",
  mobile: "9876543210",
  profile_img: "",
};

type User = {
  id?: number | string;
  name?: string;
  profile_img?: string;
  email?: string;
  mobile?: string;
  [key: string]: any;
};

type Notification = {
  id: string;
  title: string;
  description?: string;
  created_at: string;
  read?: boolean;
  type: string;
};

const DUMMY_NOTIFICATIONS: Notification[] = [
  {
    id: "party_1",
    title: "Party Added",
    description: `Party "Social Circle" was added.`,
    created_at: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    read: false,
    type: "party",
  },
  {
    id: "stock_2",
    title: "Stock Added",
    description: `Stock "Apple Inc." was added.`,
    created_at: new Date(Date.now() - 1000 * 60 * 48).toISOString(),
    read: false,
    type: "stock",
  },
  {
    id: "withdrawal_3",
    title: "Amount Withdrawn",
    description: `An amount of ₹3000 was withdrawn.`,
    created_at: new Date(Date.now() - 1000 * 60 * 98).toISOString(),
    read: false,
    type: "withdrawal",
  },
];

interface HeaderProps {
  searchValue?: string;
  onSearchChange?: (val: string) => void;
}

const USER_LS_KEY = "demo_user";
const NOTIF_LS_KEY = "seen_notif_ids_demo";

// Provide: get/set for user from localStorage, else fallback dummy
function getStoredUser(): User {
  try {
    const s = localStorage.getItem(USER_LS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return { ...DUMMY_USER };
}
function saveStoredUser(u: User) {
  try {
    localStorage.setItem(USER_LS_KEY, JSON.stringify(u));
  } catch {}
}

// Provide: get seen notif IDs from localStorage
function getSeenNotifIds(): string[] {
  try {
    const s = localStorage.getItem(NOTIF_LS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return [];
}
function markNotifAsSeen(id: string) {
  let seen = getSeenNotifIds();
  if (!seen.includes(id)) {
    seen.push(id);
    try {
      localStorage.setItem(NOTIF_LS_KEY, JSON.stringify(seen));
    } catch {}
  }
}

const Header: React.FC<HeaderProps> = ({
  searchValue = "",
  onSearchChange = () => {},
}) => {
  const [user, setUser] = useState<User>(getStoredUser());
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO);
  const [notifications, setNotifications] = useState<Notification[]>(
    DUMMY_NOTIFICATIONS
  );
  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // On mount: simulate logo load (without API)
  useEffect(() => {
    // Could allow manual change
    setLogoUrl(DEFAULT_LOGO);
  }, []);

  // On mount: hydrate from localStorage
  useEffect(() => {
    setUser(getStoredUser());
    // Simulate fetching seen notifs
    const seen = getSeenNotifIds();
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: seen.includes(n.id) }))
    );
  }, []);

  // Dismiss notification dropdown on outside click
  useEffect(() => {
    if (!showNotif) return;
    const handler = (e: MouseEvent) => {
      if (
        notifRef.current &&
        e.target instanceof Node &&
        !notifRef.current.contains(e.target)
      ) {
        setShowNotif(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotif]);

  // Mark notification as read in UI and in localStorage
  function markNotifRead(id: string) {
    setNotifications((nArr) =>
      nArr.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    markNotifAsSeen(id);
  }

  // --- Edit Profile logic ---
  function openEdit() {
    if (user) {
      setEditUser({ ...user });
      setShowEdit(true);
      setEditError(null);
    }
  }
  function closeEdit() {
    setShowEdit(false);
    setEditError(null);
  }
  function handleEditInput(e: React.ChangeEvent<HTMLInputElement>) {
    setEditUser((prev) =>
      prev ? { ...prev, [e.target.name]: e.target.value } : prev
    );
  }
  function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    setIsSubmitting(true);
    setEditError(null);
    setTimeout(() => {
      setUser(editUser);
      saveStoredUser(editUser);
      setShowEdit(false);
      setIsSubmitting(false);
    }, 700); // simulate delay
  }

  // UI presentation
  const userName = user?.name || "User";
  const userAvatar = user?.profile_img
    ? user.profile_img
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}`;
  const unreadTotal = notifications.filter((n) => !n.read).length;
  const sortedNotifs = [...notifications].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <>
      <header className="flex flex-wrap justify-between items-center p-4 gap-y-4 border-[2px] border-[#ECEFF9]">
        <div className="w-full md:w-[48%] lg:w-[16%] flex-shrink-0 ">
          <img
            className="w-[81px] h-[26px] object-contain"
            src={logoUrl}
            alt="Company Logo"
          />
        </div>
        <div className="w-full md:w-[48%] lg:w-[22%] flex-shrink-0 hidden">
          <h1 className="text-[20px] sm:text-[22px] font-semibold">
            Welcome Back, {userName}!
          </h1>
        </div>
        <div className="hidden w-full md:w-[48%] lg:w-[27%] bg-[#F4F6F8] h-[38px] flex items-center rounded-[6px] px-4">
          <input
            type="text"
            placeholder="Search..."
            className="w-full bg-transparent h-[32px] focus:outline-none"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <CiSearch className="text-[#8488B7] text-[24px] cursor-pointer" />
        </div>
        <div className="w-full md:w-[48%] lg:w-[24%] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <img
              className="w-[40px] h-[40px] object-cover rounded-full"
              src={userAvatar}
              alt="Profile"
            />
            <div className="flex flex-col">
              <span className="hidden sm:inline text-sm sm:text-base font-poppins">
                {userName}
              </span>
              <button
                className="text-xs text-blue-600 mt-0.5 underline text-left"
                style={{
                  padding: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
                onClick={openEdit}
              >
                Edit Profile
              </button>
            </div>
          </div>
          <div className="relative">
            <button
              type="button"
              aria-label={`Show notifications (${unreadTotal})`}
              onClick={() => setShowNotif((v) => !v)}
              className="relative p-1"
            >
              <CiBellOn className="text-[24px] text-[#3F4765]" />
              {unreadTotal > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 rounded-full">
                  {unreadTotal > 9 ? "9+" : unreadTotal}
                </span>
              )}
            </button>
            {showNotif && (
              <div
                ref={notifRef}
                className="absolute right-0 z-20 mt-2 w-[340px] max-w-[95vw] bg-white border border-gray-200 rounded-md shadow-lg overflow-auto max-h-[460px]"
              >
                <div className="font-semibold text-base px-4 py-2 border-b bg-[#F9F5FF] text-[#6742c7]">
                  Notifications
                </div>
                {sortedNotifs.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 text-sm">
                    No notifications yet.
                  </div>
                ) : (
                  <ul>
                    {sortedNotifs.map((n) => (
                      <li
                        key={n.id}
                        className={`px-4 py-3 border-b last:border-b-0 cursor-pointer flex gap-2 ${
                          n.read ? "" : "bg-[#F5FAFC]"
                        }`}
                        onClick={() => markNotifRead(n.id)}
                      >
                        <div className="flex-1">
                          <div
                            className={`font-semibold ${
                              n.read ? "text-gray-700" : "text-[#244B74]"
                            }`}
                          >
                            {n.title}
                          </div>
                          {n.description && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {n.description}
                            </div>
                          )}
                          <div className="text-[11px] text-gray-400 mt-1">
                            {new Date(n.created_at).toLocaleString("en-IN", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                        {!n.read && (
                          <span className="h-3 w-3 rounded-full bg-blue-500 self-start mt-1" />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="p-2 text-center">
                  <button
                    className="text-[#6742c7] text-xs underline font-medium"
                    onClick={() => setShowNotif(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {/* --- Edit Profile Modal --- */}
      {showEdit && editUser && (
        <div
          className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-40"
          style={{ animation: "fadeIn .2s" }}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full border shadow-md relative">
            <button
              onClick={closeEdit}
              className="absolute right-3 top-2 text-gray-400 hover:text-gray-700 text-lg"
              aria-label="Close"
              style={{ background: "none", border: "none" }}
            >
              &times;
            </button>
            <h2 className="font-bold text-xl mb-4 text-[#6742c7]">
              Edit Profile
            </h2>
            <form onSubmit={submitEdit} className="flex flex-col gap-3">
              <label className="text-sm font-medium flex flex-col gap-1">
                Name
                <input
                  name="name"
                  type="text"
                  value={editUser.name || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                  required
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Email
                <input
                  name="email"
                  type="email"
                  value={editUser.email || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Mobile
                <input
                  name="mobile"
                  type="text"
                  value={editUser.mobile || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Profile Image URL
                <input
                  name="profile_img"
                  type="url"
                  value={editUser.profile_img || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                  placeholder="https://example.com/img.png"
                />
              </label>
              {editError && (
                <div className="text-red-600 text-xs">{editError}</div>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-3 py-1 border rounded text-gray-600 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#6742c7] text-white px-3 py-1 rounded hover:bg-[#5432a7] disabled:opacity-50"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;

